'use client';

import { BoxScrolled, usePortalContext } from '@cloai/uikit';
import _ from 'lodash';
import { SyntheticEvent } from 'react';

import { PORTALS } from '@/.constants/portals';
import AiVideosIcon from '@/public/icons/ai_videos.svg';
import { SeeAllButton } from '@/src/components/SeeAllButton';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
  TAiVideo,
  AiVideosStyleCategory,
} from '@/src/providers';
import { getImageByGender } from '@/src/utils/getImageByGender';

import { PackPreview } from './PackPreview';

const styles = {
  wrapper: 'flex flex-col gap-3 flex-1',
  line: 'flex items-center gap-2',
  lineTitle: 'text-lg font-bold',
  lineIcon: 'w-4 h-4',
  lineCount: 'text-sm font-medium text-label-secondary',
  category: 'flex flex-col gap-3',
  head: 'flex items-center justify-between',
  name: 'text-lg font-bold',
  styleItem: 'shrink-0 relative overflow-hidden rounded-lg',
  preview: 'w-32 h-[152px] object-cover object-top',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  info: 'absolute left-3 bottom-3',
  styleName: 'mb-[2px] text-sm font-medium text-left',
};

export const VideoCategories = () => {
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const { openPortal } = usePortalContext();
  const {
    videoStyleCategories,
    changeActiveCategory,
    changeActiveStyle,
    genderMode,
    setPortalSeeAllMode,
    setPrevPortal,
  } = useStyleContext();
  const vitrinaPosition = useVitrinaPosition('videoStyleCategories');

  const handleClickPack = (event: SyntheticEvent) => {
    const categoryId = event.currentTarget.getAttribute('data-category-id');
    if (categoryId) {
      changeActiveCategory(categoryId, 'video');
    }

    setPrevPortal('main');
    setPortalSeeAllMode('videoStyleCategories');
    openPortal(PORTALS.SeeAllImages);
  };

  const handleClickImage = (event: SyntheticEvent) => {
    const categoryId = event.currentTarget.getAttribute('data-category-id');
    const currentIndex = event.currentTarget.getAttribute('data-style-ind');
    let position;

    if (categoryId) {
      changeActiveCategory(categoryId, 'video');
    }

    if (currentIndex) {
      position = Number(currentIndex);
    }

    const styleId = event.currentTarget.getAttribute('data-style-id');
    if (styleId) {
      changeActiveStyle(styleId, 'videoStyleCategories');
    }

    const currentCategory = _.find(videoStyleCategories, (category) =>
      _.find(category.aiVideos, { id: styleId }),
    );

    const currentStyle = _.find(_.get(currentCategory, 'aiVideos', []), {
      id: styleId,
    });

    if (currentCategory && currentStyle) {
      sendGTM({
        event: 'menu_selection',
        type: (currentStyle as TAiVideo).__typename,
        block: 'Видео',
        category_name: (currentCategory as AiVideosStyleCategory).name['en'],
        name: (currentStyle as TAiVideo).name['en'],
        category_position: position,
        vitrina_position: vitrinaPosition,
        gender: genderMode,
      });
    }

    setPrevPortal('main');
    setPortalSeeAllMode('videoStyleCategories');
    openPortal(PORTALS.GenerateImage);
  };

  if (!videoStyleCategories?.length) {
    return;
  }

  return (
    <div className={styles.wrapper}>
      {videoStyleCategories?.map((category, ind) => (
        <div className={styles.category} key={category.id}>
          <div className={styles.head}>
            <div className={styles.line}>
              <AiVideosIcon className={styles.lineIcon} />
              <div className={styles.name}>{category.name[profile?.lang]}</div>
            </div>

            <SeeAllButton onClick={handleClickPack} categoryId={category.id} />
          </div>
          <BoxScrolled
            direction="horizontal"
            hideScrollbar
            gap={8}
            offsetOut={20}
          >
            {category.aiVideos.map((item, i) => (
              <PackPreview
                key={item.id}
                id={item.id}
                index={i}
                categoryIndex={ind}
                categoryId={category.id}
                isPopular={item.isPopular}
                isNew={item.isNew}
                name={item.name[profile?.lang]}
                image={getImageByGender(item, genderMode) ?? ''}
                handleClickImage={handleClickImage}
                isVideo
              />
            ))}
          </BoxScrolled>
        </div>
      ))}
    </div>
  );
};
